import React from "react";
import { Coins, PackageCheck, Plus, Trash2, Truck, Warehouse } from "lucide-react";

export default function PaymentDeliverySettings({
  mode = "all",
  activeValues,
  setActiveValues,
  warehouseOptions = [],
  restaurantOptions = [],
  Field,
  TextInput,
  SelectInput,
  Toggle,
  SectionCard,
  parseNumberField,
  updateDeliveryFeeWarehouseOverride,
  addDeliveryFeeWarehouseOverride,
  removeDeliveryFeeWarehouseOverride,
  updateDriverEarningWarehouseOverride,
  addDriverEarningWarehouseOverride,
  removeDriverEarningWarehouseOverride,
  updateHandlingChargeWarehouseOverride,
  addHandlingChargeWarehouseOverride,
  removeHandlingChargeWarehouseOverride,
  updateRainChargeWarehouseOverride,
  addRainChargeWarehouseOverride,
  removeRainChargeWarehouseOverride,
  updateSurgeChargeWarehouseOverride,
  addSurgeChargeWarehouseOverride,
  removeSurgeChargeWarehouseOverride,
  updatePayOnDeliveryWarehouseOverride,
  addPayOnDeliveryWarehouseOverride,
  removePayOnDeliveryWarehouseOverride,
  updatePayOnDeliveryRestaurantOverride,
  addPayOnDeliveryRestaurantOverride,
  removePayOnDeliveryRestaurantOverride,
  borderColor,
}) {
  const showDeliveryControls = mode !== "charges";
  const showChargeControls = mode !== "delivery";

  return (
    <div className="grid gap-5 xl:grid-cols-2">

      {showDeliveryControls ? (
      <SectionCard
        icon={PackageCheck}
        title="Pay on Delivery Module"
        description="Global master switch. ON shows POD in the customer app and allows new POD checkout APIs. OFF hides POD and blocks every new POD checkout. Existing POD orders remain collectable."
      >
        <div className="space-y-5">
          <Toggle
            label="Enable Pay on Delivery Globally"
            checked={!!activeValues.payOnDeliveryEnabled}
            onChange={(next) =>
              setActiveValues((prev) => ({
                ...prev,
                payOnDeliveryEnabled: next,
              }))
            }
          />

          {/* <Field label="Payment Provider">
            <TextInput
              value={activeValues.payOnDeliveryProvider || "RAZORPAY"}
              onChange={(e) =>
                setActiveValues((prev) => ({
                  ...prev,
                  payOnDeliveryProvider: e.target.value.toUpperCase(),
                }))
              }
              placeholder="RAZORPAY"
            />
          </Field> */}

          <div className="rounded-[16px] border bg-[#fff8f9] px-4 py-3 text-xs font-medium leading-5 text-slate-600" style={{ borderColor }}>
            Global OFF always wins: it hides POD from the customer UI and blocks new POD orders everywhere. Keep Global ON, then turn a warehouse OFF below when only that warehouse must not accept POD. Existing POD orders can still be collected by driver QR.
          </div>
        </div>
      </SectionCard>
      ) : null}

      {showDeliveryControls ? (
      <SectionCard
        icon={Warehouse}
        title="Warehouse-wise Pay on Delivery"
        description="With Global POD ON, turn selected warehouses OFF. Warehouses without an override inherit the global ON state."
      >
        <div className="space-y-4">
          {(activeValues.payOnDeliveryWarehouseOverrides || []).length ? (
            <div className="space-y-3">
              {(activeValues.payOnDeliveryWarehouseOverrides || []).map((row, index) => (
                <div
                  key={`pod-warehouse-${index}`}
                  className="rounded-[18px] border p-3"
                  style={{ borderColor }}
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <Toggle
                      label="POD Enabled for Warehouse"
                      checked={!!row.enabled}
                      onChange={(next) =>
                        updatePayOnDeliveryWarehouseOverride(index, "enabled", next)
                      }
                    />
                    <button
                      type="button"
                      onClick={() => removePayOnDeliveryWarehouseOverride(index)}
                      className="inline-flex h-[44px] items-center justify-center rounded-[14px] border px-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                      style={{ borderColor }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <Field label="Warehouse">
                    <SelectInput
                      value={row.warehouseId}
                      onChange={(e) =>
                        updatePayOnDeliveryWarehouseOverride(index, "warehouseId", e.target.value)
                      }
                      options={[
                        { label: "Select warehouse", value: "" },
                        ...warehouseOptions.map((warehouse) => ({
                          label: `${warehouse.name}${warehouse.city ? ` (${warehouse.city})` : ""}`,
                          value: warehouse.id,
                        })),
                      ]}
                    />
                  </Field>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="rounded-[18px] border border-dashed px-4 py-6 text-center text-sm text-slate-500"
              style={{ borderColor }}
            >
              No warehouse override added. Every warehouse currently inherits the global Pay on Delivery state.
            </div>
          )}

          <button
            type="button"
            onClick={addPayOnDeliveryWarehouseOverride}
            className="inline-flex items-center gap-2 rounded-[14px] border px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            style={{ borderColor }}
          >
            <Plus size={16} />
            Disable POD for a Warehouse
          </button>
        </div>
      </SectionCard>
      ) : null}

      {showChargeControls ? (
      <SectionCard
        icon={PackageCheck}
        title="Restaurant-wise Pay on Delivery"
        description="Override food/restaurant Pay on Delivery per restaurant."
      >
        <div className="space-y-4">
          {(activeValues.payOnDeliveryRestaurantOverrides || []).length ? (
            <div className="space-y-3">
              {(activeValues.payOnDeliveryRestaurantOverrides || []).map((row, index) => (
                <div
                  key={`pod-restaurant-${index}`}
                  className="rounded-[18px] border p-3"
                  style={{ borderColor }}
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <Toggle
                      label="Enabled"
                      checked={!!row.enabled}
                      onChange={(next) =>
                        updatePayOnDeliveryRestaurantOverride(index, "enabled", next)
                      }
                    />
                    <button
                      type="button"
                      onClick={() => removePayOnDeliveryRestaurantOverride(index)}
                      className="inline-flex h-[44px] items-center justify-center rounded-[14px] border px-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                      style={{ borderColor }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <Field label="Restaurant">
                    <SelectInput
                      value={row.restaurantId}
                      onChange={(e) =>
                        updatePayOnDeliveryRestaurantOverride(index, "restaurantId", e.target.value)
                      }
                      options={[
                        { label: "Select restaurant", value: "" },
                        ...restaurantOptions.map((restaurant) => ({
                          label: `${restaurant.name}${restaurant.city ? ` (${restaurant.city})` : ""}`,
                          value: restaurant.id,
                        })),
                      ]}
                    />
                  </Field>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="rounded-[18px] border border-dashed px-4 py-6 text-center text-sm text-slate-500"
              style={{ borderColor }}
            >
              No restaurant Pay on Delivery override added. Global Pay on Delivery rule will apply to food orders.
            </div>
          )}

          <button
            type="button"
            onClick={addPayOnDeliveryRestaurantOverride}
            className="inline-flex items-center gap-2 rounded-[14px] border px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            style={{ borderColor }}
          >
            <Plus size={16} />
            Add Restaurant POD Override
          </button>
        </div>
      </SectionCard>
      ) : null}
      {showChargeControls ? (
      <SectionCard
        icon={PackageCheck}
        title="Grocery Small Cart Charge"
        description="Keep the existing grocery minimum as the small-cart threshold. When Small Cart Charge is enabled, customers below the threshold can still checkout with the configured fixed charge."
      >
        <div className="space-y-4">
          <Toggle
            label="Allow below-minimum orders with Small Cart Charge"
            checked={!!activeValues.grocerySmallCartChargeEnabled}
            onChange={(next) =>
              setActiveValues((prev) => ({
                ...prev,
                grocerySmallCartChargeEnabled: next,
              }))
            }
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Small Cart Threshold"
              hint="₹ · merchandise subtotal"
              info="The existing grocery minimum-order value is reused as the threshold. Delivery, handling, rain, surge, tax and coupon discount do not count toward it."
            >
              <TextInput
                type="number"
                min={0}
                step="1"
                value={activeValues.groceryMinimumOrderValue}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    groceryMinimumOrderValue: parseNumberField(e.target.value),
                  }))
                }
                placeholder="199"
              />
            </Field>

            <Field
              label="Small Cart Charge"
              hint="₹ · fixed charge"
              info="Applied only when the merchandise subtotal is below the threshold and the Small Cart Charge toggle is enabled."
            >
              <TextInput
                type="number"
                min={0}
                step="1"
                value={activeValues.grocerySmallCartChargeAmount}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    grocerySmallCartChargeAmount: parseNumberField(e.target.value),
                  }))
                }
                placeholder="20"
              />
            </Field>
          </div>

          <div
            className="rounded-[16px] border bg-slate-50 px-4 py-3 text-xs font-medium leading-5 text-slate-600"
            style={{ borderColor }}
          >
            Example: threshold ₹199, charge ₹20, cart ₹170 → checkout allowed with ₹20 Small Cart Charge. If the toggle is OFF, the existing hard minimum-order block remains unchanged.
          </div>
        </div>
      </SectionCard>
      ) : null}

      {showChargeControls ? (
      <SectionCard
        icon={Coins}
        title="Delivery Fee Rules"
        description="Global fee applies to every warehouse. Add warehouse override when one warehouse needs different ₹/km or free delivery threshold."
      >
        <div className="space-y-5">
          <Toggle
            label="Enable Delivery Fee"
            checked={!!activeValues.deliveryFeeEnabled}
            onChange={(next) =>
              setActiveValues((prev) => ({
                ...prev,
                deliveryFeeEnabled: next,
              }))
            }
          />

          <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2 xl:grid-cols-4">

            <Field label="Base Km">
              <TextInput
                type="number"
                min={0}
                value={activeValues.deliveryFeeBaseKm}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    deliveryFeeBaseKm: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Base Fee">
              <TextInput
                type="number"
                min={0}
                value={activeValues.deliveryFeeBaseFee}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    deliveryFeeBaseFee: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Per Km Charge">
              <TextInput
                type="number"
                min={0}
                value={activeValues.deliveryFeePerKmCharge}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    deliveryFeePerKmCharge: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Minimum Fee">
              <TextInput
                type="number"
                min={0}
                value={activeValues.deliveryFeeMinimumFee}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    deliveryFeeMinimumFee: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Max Fee">
              <TextInput
                type="number"
                min={0}
                value={activeValues.deliveryFeeMaxFee}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    deliveryFeeMaxFee: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Free Delivery Above">
              <TextInput
                type="number"
                min={0}
                value={activeValues.deliveryFeeFreeDeliveryAbove}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    deliveryFeeFreeDeliveryAbove: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Round To">
              <TextInput
                type="number"
                min={0}
                value={activeValues.deliveryFeeRoundTo}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    deliveryFeeRoundTo: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>
          </div>
        </div>
      </SectionCard>
      ) : null}

      {showChargeControls ? (
      <SectionCard
        icon={Truck}
        title="Driver Earning Rules"
        description="This is separate from customer delivery fee. Use it when customer delivery fee is ₹0/free but driver still needs payout."
      >
        <div className="space-y-5">
          <Toggle
            label="Enable Driver Earning"
            checked={!!activeValues.driverEarningEnabled}
            onChange={(next) =>
              setActiveValues((prev) => ({
                ...prev,
                driverEarningEnabled: next,
              }))
            }
          />

          <div className="rounded-[16px] border bg-[#fff8f9] px-4 py-3 text-xs font-semibold leading-5 text-slate-600" style={{ borderColor }}>
            Customer delivery fee affects the user bill. Driver earning affects wallet payout/admin cost only. Never use Delivery Fee as driver payout for free delivery campaigns.
          </div>

          <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2 xl:grid-cols-4">

            <Field label="Mode">
              <SelectInput
                value={activeValues.driverEarningMode || "DISTANCE_BASED"}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    driverEarningMode: e.target.value,
                  }))
                }
                options={[
                  { label: "Distance Based", value: "DISTANCE_BASED" },
                  { label: "Fixed Amount", value: "FIXED" },
                ]}
              />
            </Field>

            <Field label="Fixed Amount" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.driverEarningFixedAmount}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    driverEarningFixedAmount: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Base Km">
              <TextInput
                type="number"
                min={0}
                value={activeValues.driverEarningBaseKm}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    driverEarningBaseKm: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Base Earning" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.driverEarningBaseFee}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    driverEarningBaseFee: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Per Km Earning" hint="₹/km">
              <TextInput
                type="number"
                min={0}
                value={activeValues.driverEarningPerKmCharge}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    driverEarningPerKmCharge: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Minimum Earning" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.driverEarningMinimumFee}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    driverEarningMinimumFee: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Max Earning" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.driverEarningMaxFee}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    driverEarningMaxFee: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Round To" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.driverEarningRoundTo}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    driverEarningRoundTo: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-slate-800">Warehouse Overrides</div>
                <div className="text-xs text-slate-500">Use only when one warehouse needs different driver payout.</div>
              </div>
              <button
                type="button"
                onClick={addDriverEarningWarehouseOverride}
                className="inline-flex items-center gap-2 rounded-[14px] border px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                style={{ borderColor }}
              >
                <Plus size={16} />
                Add Override
              </button>
            </div>

            {(activeValues.driverEarningWarehouseOverrides || []).length ? (
              <div className="space-y-3">
                {(activeValues.driverEarningWarehouseOverrides || []).map((row, index) => (
                  <div key={`driver-earning-warehouse-${index}`} className="rounded-[18px] border p-3" style={{ borderColor }}>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <Toggle
                        label="Driver Earning Enabled"
                        checked={!!row.enabled}
                        onChange={(next) =>
                          updateDriverEarningWarehouseOverride(index, "enabled", next)
                        }
                      />
                      <button
                        type="button"
                        onClick={() => removeDriverEarningWarehouseOverride(index)}
                        className="inline-flex h-[44px] items-center justify-center rounded-[14px] border px-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                        style={{ borderColor }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                      <Field label="Warehouse">
                        <SelectInput
                          value={row.warehouseId}
                          onChange={(e) =>
                            updateDriverEarningWarehouseOverride(index, "warehouseId", e.target.value)
                          }
                          options={[
                            { label: "Select warehouse", value: "" },
                            ...warehouseOptions.map((warehouse) => ({
                              label: `${warehouse.name}${warehouse.city ? ` (${warehouse.city})` : ""}`,
                              value: warehouse.id,
                            })),
                          ]}
                        />
                      </Field>

                      <Field label="Mode">
                        <SelectInput
                          value={row.mode || "DISTANCE_BASED"}
                          onChange={(e) =>
                            updateDriverEarningWarehouseOverride(index, "mode", e.target.value)
                          }
                          options={[
                            { label: "Distance Based", value: "DISTANCE_BASED" },
                            { label: "Fixed Amount", value: "FIXED" },
                          ]}
                        />
                      </Field>

                      <Field label="Fixed Amount" hint="₹">
                        <TextInput type="number" min={0} value={row.fixedAmount} onChange={(e) => updateDriverEarningWarehouseOverride(index, "fixedAmount", e.target.value)} />
                      </Field>

                      <Field label="Base Km">
                        <TextInput type="number" min={0} value={row.baseKm} onChange={(e) => updateDriverEarningWarehouseOverride(index, "baseKm", e.target.value)} />
                      </Field>

                      <Field label="Base Earning" hint="₹">
                        <TextInput type="number" min={0} value={row.baseFee} onChange={(e) => updateDriverEarningWarehouseOverride(index, "baseFee", e.target.value)} />
                      </Field>

                      <Field label="Per Km" hint="₹/km">
                        <TextInput type="number" min={0} value={row.perKmCharge} onChange={(e) => updateDriverEarningWarehouseOverride(index, "perKmCharge", e.target.value)} />
                      </Field>

                      <Field label="Minimum" hint="₹">
                        <TextInput type="number" min={0} value={row.minimumFee} onChange={(e) => updateDriverEarningWarehouseOverride(index, "minimumFee", e.target.value)} />
                      </Field>

                      <Field label="Max" hint="₹">
                        <TextInput type="number" min={0} value={row.maxFee} onChange={(e) => updateDriverEarningWarehouseOverride(index, "maxFee", e.target.value)} />
                      </Field>

                      <Field label="Round To" hint="₹">
                        <TextInput type="number" min={0} value={row.roundTo} onChange={(e) => updateDriverEarningWarehouseOverride(index, "roundTo", e.target.value)} />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="rounded-[18px] border border-dashed px-4 py-6 text-center text-sm text-slate-500"
                style={{ borderColor }}
              >
                No driver earning override added. Every warehouse uses the global driver earning rule.
              </div>
            )}
          </div>
        </div>
      </SectionCard>
      ) : null}


      {showChargeControls ? (
      <SectionCard
        icon={PackageCheck}
        title="Grocery Handling Charge"
        description="Weight-based charge for grocery orders. Total order weight is calculated from product/variant weight saved in cart checkout."
      >
        <div className="space-y-5">
          <Toggle
            label="Enable Handling Charge"
            checked={!!activeValues.handlingChargeEnabled}
            onChange={(next) =>
              setActiveValues((prev) => ({
                ...prev,
                handlingChargeEnabled: next,
              }))
            }
          />

          <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2 xl:grid-cols-4">

            <Field
              label="Free Weight Up To"
              hint="Kg"
              info="No handling charge is added until total cart weight crosses this kg limit."
            >
              <TextInput
                type="number"
                min={0}
                value={activeValues.handlingChargeFreeUpToKg}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    handlingChargeFreeUpToKg: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Base Charge" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.handlingChargeBaseCharge}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    handlingChargeBaseCharge: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Per Kg Charge" hint="₹/kg">
              <TextInput
                type="number"
                min={0}
                value={activeValues.handlingChargePerKgCharge}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    handlingChargePerKgCharge: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Minimum Charge" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.handlingChargeMinimumCharge}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    handlingChargeMinimumCharge: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Max Charge" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.handlingChargeMaxCharge}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    handlingChargeMaxCharge: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field
              label="Round Weight To"
              hint="Kg"
              info="Example: 0.5 means 2.1 kg will charge as 2.5 kg."
            >
              <TextInput
                type="number"
                min={0}
                value={activeValues.handlingChargeRoundWeightToKg}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    handlingChargeRoundWeightToKg: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Round Charge To" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.handlingChargeRoundTo}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    handlingChargeRoundTo: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field
              label="Default Missing Item Weight"
              hint="Kg"
              info="Use 0 to avoid guessing. If product weight is missing, this fallback will be used per quantity."
            >
              <TextInput
                type="number"
                min={0}
                value={activeValues.handlingChargeDefaultItemWeightKg}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    handlingChargeDefaultItemWeightKg: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>
          </div>
        </div>
      </SectionCard>
      ) : null}

      {showChargeControls ? (
      <SectionCard
        icon={Warehouse}
        title="Warehouse-wise Handling Charge Overrides"
        description="Override the existing weight-based handling rule only for selected warehouses. Warehouses without an override keep the global rule."
      >
        <div className="space-y-4">
          {(activeValues.handlingChargeWarehouseOverrides || []).length ? (
            <div className="space-y-3">
              {(activeValues.handlingChargeWarehouseOverrides || []).map((row, index) => (
                <div key={`handling-charge-${index}`} className="rounded-[18px] border p-3" style={{ borderColor }}>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <Toggle label="Enabled" checked={!!row.enabled} onChange={(next) => updateHandlingChargeWarehouseOverride(index, "enabled", next)} />
                    <button type="button" onClick={() => removeHandlingChargeWarehouseOverride(index)} className="inline-flex h-[44px] items-center justify-center rounded-[14px] border px-3 text-rose-600 hover:bg-rose-50" style={{ borderColor }}><Trash2 size={16} /></button>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <Field label="Warehouse"><SelectInput value={row.warehouseId} onChange={(e) => updateHandlingChargeWarehouseOverride(index, "warehouseId", e.target.value)} options={[{ label: "Select warehouse", value: "" }, ...warehouseOptions.map((w) => ({ label: `${w.name}${w.city ? ` (${w.city})` : ""}`, value: w.id }))]} /></Field>
                    <Field label="Free Weight Up To" hint="Kg"><TextInput type="number" min={0} value={row.freeUpToKg} onChange={(e) => updateHandlingChargeWarehouseOverride(index, "freeUpToKg", e.target.value)} /></Field>
                    <Field label="Base Charge" hint="₹"><TextInput type="number" min={0} value={row.baseCharge} onChange={(e) => updateHandlingChargeWarehouseOverride(index, "baseCharge", e.target.value)} /></Field>
                    <Field label="Per Kg Charge" hint="₹/kg"><TextInput type="number" min={0} value={row.perKgCharge} onChange={(e) => updateHandlingChargeWarehouseOverride(index, "perKgCharge", e.target.value)} /></Field>
                    <Field label="Minimum Charge" hint="₹"><TextInput type="number" min={0} value={row.minimumCharge} onChange={(e) => updateHandlingChargeWarehouseOverride(index, "minimumCharge", e.target.value)} /></Field>
                    <Field label="Max Charge" hint="₹"><TextInput type="number" min={0} value={row.maxCharge} onChange={(e) => updateHandlingChargeWarehouseOverride(index, "maxCharge", e.target.value)} /></Field>
                    <Field label="Round Weight To" hint="Kg"><TextInput type="number" min={0} value={row.roundWeightToKg} onChange={(e) => updateHandlingChargeWarehouseOverride(index, "roundWeightToKg", e.target.value)} /></Field>
                    <Field label="Round Charge To" hint="₹"><TextInput type="number" min={0} value={row.roundTo} onChange={(e) => updateHandlingChargeWarehouseOverride(index, "roundTo", e.target.value)} /></Field>
                    <Field label="Default Missing Weight" hint="Kg"><TextInput type="number" min={0} value={row.defaultItemWeightKg} onChange={(e) => updateHandlingChargeWarehouseOverride(index, "defaultItemWeightKg", e.target.value)} /></Field>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="rounded-[18px] border border-dashed px-4 py-6 text-center text-sm text-slate-500" style={{ borderColor }}>No handling charge override added. Global handling rule applies to every warehouse.</div>}
          <button type="button" onClick={addHandlingChargeWarehouseOverride} className="inline-flex items-center gap-2 rounded-[14px] border px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50" style={{ borderColor }}><Plus size={16} />Add Handling Override</button>
        </div>
      </SectionCard>
      ) : null}

      {showChargeControls ? (
      <SectionCard
        icon={Coins}
        title="Grocery Rain Charge"
        description="Weather-based extra charge for grocery orders. It applies only when Google Weather reports rain near the customer's delivery location. Warehouse overrides can turn it on/off per warehouse."
      >
        <div className="space-y-5">
          <Toggle
            label="Enable Global Rain Charge"
            checked={!!activeValues.rainChargeEnabled}
            onChange={(next) =>
              setActiveValues((prev) => ({ ...prev, rainChargeEnabled: next }))
            }
          />

          <Field label="Activation Mode" info="AUTO keeps the current Google Weather behavior. FORCE ON applies the configured charge without calling Weather API.">
            <SelectInput value={activeValues.rainChargeActivationMode || "AUTO"} onChange={(e) => setActiveValues((prev) => ({ ...prev, rainChargeActivationMode: e.target.value }))} options={[{ label: "Auto - Google Weather", value: "AUTO" }, { label: "Force On - Manual", value: "FORCE_ON" }]} />
          </Field>

          <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2 xl:grid-cols-4">

            <Field label="Rain Charge Amount" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.rainChargeAmount}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    rainChargeAmount: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Min Order Value" hint="₹">
              <TextInput
                type="number"
                min={0}
                value={activeValues.rainChargeMinOrderValue}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    rainChargeMinOrderValue: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Min Rain Probability" hint="%">
              <TextInput
                type="number"
                min={0}
                max={100}
                value={activeValues.rainChargeMinProbabilityPercent}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    rainChargeMinProbabilityPercent: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>

            <Field label="Weather Cache TTL" hint="seconds">
              <TextInput
                type="number"
                min={30}
                value={activeValues.rainChargeCacheTtlSeconds}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    rainChargeCacheTtlSeconds: parseNumberField(e.target.value),
                  }))
                }
              />
            </Field>
          </div>
        </div>
      </SectionCard>
      ) : null}

      {showChargeControls ? (
      <SectionCard
        icon={Warehouse}
        title="Warehouse-wise Rain Charge Overrides"
        description="Override grocery rain charge per warehouse. Use this when one warehouse wants rain charge ON and another wants it OFF."
      >
        <div className="space-y-4">
          {(activeValues.rainChargeWarehouseOverrides || []).length ? (
            <div className="space-y-3">
              {(activeValues.rainChargeWarehouseOverrides || []).map((row, index) => (
                <div
                  key={`rain-charge-warehouse-${index}`}
                  className="rounded-[18px] border p-3"
                  style={{ borderColor }}
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <Toggle
                      label="Enabled"
                      checked={!!row.enabled}
                      onChange={(next) =>
                        updateRainChargeWarehouseOverride(index, "enabled", next)
                      }
                    />
                    <button
                      type="button"
                      onClick={() => removeRainChargeWarehouseOverride(index)}
                      className="inline-flex h-[44px] items-center justify-center rounded-[14px] border px-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                      style={{ borderColor }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <Field label="Warehouse">
                      <SelectInput
                        value={row.warehouseId}
                        onChange={(e) =>
                          updateRainChargeWarehouseOverride(index, "warehouseId", e.target.value)
                        }
                        options={[
                          { label: "Select warehouse", value: "" },
                          ...warehouseOptions.map((warehouse) => ({
                            label: `${warehouse.name}${warehouse.city ? ` (${warehouse.city})` : ""}`,
                            value: warehouse.id,
                          })),
                        ]}
                      />
                    </Field>
                    <Field label="Activation Mode"><SelectInput value={row.activationMode || "AUTO"} onChange={(e) => updateRainChargeWarehouseOverride(index, "activationMode", e.target.value)} options={[{ label: "Auto - Google Weather", value: "AUTO" }, { label: "Force On - Manual", value: "FORCE_ON" }]} /></Field>
                    <Field label="Amount" hint="₹">
                      <TextInput
                        type="number"
                        min={0}
                        value={row.amount}
                        onChange={(e) =>
                          updateRainChargeWarehouseOverride(index, "amount", e.target.value)
                        }
                      />
                    </Field>
                    <Field label="Min Order Value" hint="₹">
                      <TextInput
                        type="number"
                        min={0}
                        value={row.minOrderValue}
                        onChange={(e) =>
                          updateRainChargeWarehouseOverride(index, "minOrderValue", e.target.value)
                        }
                      />
                    </Field>
                    <Field label="Min Rain Probability" hint="%">
                      <TextInput
                        type="number"
                        min={0}
                        max={100}
                        value={row.minProbabilityPercent}
                        onChange={(e) =>
                          updateRainChargeWarehouseOverride(index, "minProbabilityPercent", e.target.value)
                        }
                      />
                    </Field>
                    <Field label="Weather Cache TTL" hint="seconds">
                      <TextInput
                        type="number"
                        min={30}
                        value={row.cacheTtlSeconds}
                        onChange={(e) =>
                          updateRainChargeWarehouseOverride(index, "cacheTtlSeconds", e.target.value)
                        }
                      />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="rounded-[18px] border border-dashed px-4 py-6 text-center text-sm text-slate-500"
              style={{ borderColor }}
            >
              No rain charge warehouse override added. Global rain charge rule will apply.
            </div>
          )}

          <button
            type="button"
            onClick={addRainChargeWarehouseOverride}
            className="inline-flex items-center gap-2 rounded-[14px] border px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            style={{ borderColor }}
          >
            <Plus size={16} />
            Add Rain Charge Override
          </button>
        </div>
      </SectionCard>
      ) : null}

      {showChargeControls ? (
      <SectionCard
        icon={Coins}
        title="Grocery Surge Charge"
        description="High-demand charge for grocery orders. AUTO uses the selected warehouse's active-order count; FORCE ON applies it manually. Orders at or above the free threshold are never charged surge."
      >
        <div className="space-y-5">
          <Toggle label="Enable Surge Charge" checked={!!activeValues.surgeChargeEnabled} onChange={(next) => setActiveValues((prev) => ({ ...prev, surgeChargeEnabled: next }))} />
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Field label="Activation Mode"><SelectInput value={activeValues.surgeChargeActivationMode || "AUTO"} onChange={(e) => setActiveValues((prev) => ({ ...prev, surgeChargeActivationMode: e.target.value }))} options={[{ label: "Auto - High Demand", value: "AUTO" }, { label: "Force On - Manual", value: "FORCE_ON" }]} /></Field>
            <Field label="Surge Amount" hint="₹"><TextInput type="number" min={0} value={activeValues.surgeChargeAmount} onChange={(e) => setActiveValues((prev) => ({ ...prev, surgeChargeAmount: parseNumberField(e.target.value) }))} /></Field>
            <Field label="Active Order Threshold" info="In AUTO mode, surge applies when active grocery orders for the warehouse are greater than or equal to this value."><TextInput type="number" min={1} value={activeValues.surgeChargeActiveOrderThreshold} onChange={(e) => setActiveValues((prev) => ({ ...prev, surgeChargeActiveOrderThreshold: parseNumberField(e.target.value) }))} /></Field>
            <Field label="Surge Free Above" hint="₹" info="Compared with merchandise subtotal before delivery, handling, rain, surge and tax. 0 disables this exemption."><TextInput type="number" min={0} value={activeValues.surgeChargeFreeAboveOrderAmount} onChange={(e) => setActiveValues((prev) => ({ ...prev, surgeChargeFreeAboveOrderAmount: parseNumberField(e.target.value) }))} /></Field>
          </div>
        </div>
      </SectionCard>
      ) : null}

      {showChargeControls ? (
      <SectionCard
        icon={Warehouse}
        title="Warehouse-wise Surge Charge Overrides"
        description="Use a warehouse override only when that warehouse needs a different surge amount, threshold, free-above amount or manual mode."
      >
        <div className="space-y-4">
          {(activeValues.surgeChargeWarehouseOverrides || []).length ? <div className="space-y-3">{(activeValues.surgeChargeWarehouseOverrides || []).map((row, index) => (
            <div key={`surge-${index}`} className="rounded-[18px] border p-3" style={{ borderColor }}>
              <div className="mb-3 flex items-center justify-between gap-3"><Toggle label="Enabled" checked={!!row.enabled} onChange={(next) => updateSurgeChargeWarehouseOverride(index, "enabled", next)} /><button type="button" onClick={() => removeSurgeChargeWarehouseOverride(index)} className="inline-flex h-[44px] items-center justify-center rounded-[14px] border px-3 text-rose-600 hover:bg-rose-50" style={{ borderColor }}><Trash2 size={16} /></button></div>
              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Warehouse"><SelectInput value={row.warehouseId} onChange={(e) => updateSurgeChargeWarehouseOverride(index, "warehouseId", e.target.value)} options={[{ label: "Select warehouse", value: "" }, ...warehouseOptions.map((w) => ({ label: `${w.name}${w.city ? ` (${w.city})` : ""}`, value: w.id }))]} /></Field>
                <Field label="Activation Mode"><SelectInput value={row.activationMode || "AUTO"} onChange={(e) => updateSurgeChargeWarehouseOverride(index, "activationMode", e.target.value)} options={[{ label: "Auto - High Demand", value: "AUTO" }, { label: "Force On - Manual", value: "FORCE_ON" }]} /></Field>
                <Field label="Amount" hint="₹"><TextInput type="number" min={0} value={row.amount} onChange={(e) => updateSurgeChargeWarehouseOverride(index, "amount", e.target.value)} /></Field>
                <Field label="Active Order Threshold"><TextInput type="number" min={1} value={row.activeOrderThreshold} onChange={(e) => updateSurgeChargeWarehouseOverride(index, "activeOrderThreshold", e.target.value)} /></Field>
                <Field label="Surge Free Above" hint="₹"><TextInput type="number" min={0} value={row.freeAboveOrderAmount} onChange={(e) => updateSurgeChargeWarehouseOverride(index, "freeAboveOrderAmount", e.target.value)} /></Field>
              </div>
            </div>
          ))}</div> : <div className="rounded-[18px] border border-dashed px-4 py-6 text-center text-sm text-slate-500" style={{ borderColor }}>No surge override added. Global surge rule applies to every warehouse.</div>}
          <button type="button" onClick={addSurgeChargeWarehouseOverride} className="inline-flex items-center gap-2 rounded-[14px] border px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50" style={{ borderColor }}><Plus size={16} />Add Surge Override</button>
        </div>
      </SectionCard>
      ) : null}

      {showChargeControls ? (
      <SectionCard
        icon={Warehouse}
        title="Warehouse-wise Delivery Fee Overrides"
        description="Select warehouse and set different charge rules. These override global delivery fee rules only for selected warehouse."
      >
        <div className="space-y-4">
          {(activeValues.deliveryFeeWarehouseOverrides || []).length ? (
            <div className="space-y-3">
              {(activeValues.deliveryFeeWarehouseOverrides || []).map((row, index) => (
                <div
                  key={`delivery-fee-warehouse-${index}`}
                  className="rounded-[18px] border p-3"
                  style={{ borderColor }}
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <Toggle
                      label="Enabled"
                      checked={!!row.enabled}
                      onChange={(next) =>
                        updateDeliveryFeeWarehouseOverride(index, "enabled", next)
                      }
                    />

                    <button
                      type="button"
                      onClick={() => removeDeliveryFeeWarehouseOverride(index)}
                      className="inline-flex h-[44px] items-center justify-center rounded-[14px] border px-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                      style={{ borderColor }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <Field label="Warehouse">
                      <SelectInput
                        value={row.warehouseId}
                        onChange={(e) =>
                          updateDeliveryFeeWarehouseOverride(index, "warehouseId", e.target.value)
                        }
                        options={[
                          { label: "Select warehouse", value: "" },
                          ...warehouseOptions.map((warehouse) => ({
                            label: `${warehouse.name}${warehouse.city ? ` (${warehouse.city})` : ""}`,
                            value: warehouse.id,
                          })),
                        ]}
                      />
                    </Field>

                    <Field label="Base Km">
                      <TextInput
                        type="number"
                        min={0}
                        value={row.baseKm}
                        onChange={(e) =>
                          updateDeliveryFeeWarehouseOverride(index, "baseKm", e.target.value)
                        }
                      />
                    </Field>

                    <Field label="Base Fee">
                      <TextInput
                        type="number"
                        min={0}
                        value={row.baseFee}
                        onChange={(e) =>
                          updateDeliveryFeeWarehouseOverride(index, "baseFee", e.target.value)
                        }
                      />
                    </Field>

                    <Field label="Per Km Charge">
                      <TextInput
                        type="number"
                        min={0}
                        value={row.perKmCharge}
                        onChange={(e) =>
                          updateDeliveryFeeWarehouseOverride(index, "perKmCharge", e.target.value)
                        }
                      />
                    </Field>

                    <Field label="Minimum Fee">
                      <TextInput
                        type="number"
                        min={0}
                        value={row.minimumFee}
                        onChange={(e) =>
                          updateDeliveryFeeWarehouseOverride(index, "minimumFee", e.target.value)
                        }
                      />
                    </Field>

                    <Field label="Max Fee">
                      <TextInput
                        type="number"
                        min={0}
                        value={row.maxFee}
                        onChange={(e) =>
                          updateDeliveryFeeWarehouseOverride(index, "maxFee", e.target.value)
                        }
                      />
                    </Field>

                    <Field label="Free Delivery Above">
                      <TextInput
                        type="number"
                        min={0}
                        value={row.freeDeliveryAbove}
                        onChange={(e) =>
                          updateDeliveryFeeWarehouseOverride(index, "freeDeliveryAbove", e.target.value)
                        }
                      />
                    </Field>

                    <Field label="Round To">
                      <TextInput
                        type="number"
                        min={0}
                        value={row.roundTo}
                        onChange={(e) =>
                          updateDeliveryFeeWarehouseOverride(index, "roundTo", e.target.value)
                        }
                      />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="rounded-[18px] border border-dashed px-4 py-6 text-center text-sm text-slate-500"
              style={{ borderColor }}
            >
              No warehouse override added. Global delivery fee rule will apply.
            </div>
          )}

          <button
            type="button"
            onClick={addDeliveryFeeWarehouseOverride}
            className="inline-flex items-center gap-2 rounded-[14px] border px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            style={{ borderColor }}
          >
            <Plus size={16} />
            Add Warehouse Override
          </button>
        </div>
      </SectionCard>
      ) : null}
    </div>
  );
}
